import React from 'react';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import { IntlShape } from 'react-intl';
import { Button } from './Button';
import { ButtonGroup } from './ButtonGroup';
import _ from 'lodash';
import './RotationAngleSelectorOverlay.scss';

const OverlayContainer = styled.div`
  pointer-events: auto;
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translate(-50%, 0);
  background-color: rgba(0, 0, 0, 0.85);
  padding: 10px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 15px;
  min-width: 400px;
  width: 80%;
  z-index: 1000;
  cursor: default;
`;

const RotationDisplay = styled.div`
  color: white;
  text-align: center;
  font-size: 2em;
  font-weight: 700;
`;

const SliderContainerWithDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 90%;
  margin-bottom: 1em;
`;

const Slider = styled.input`
  width: 100%;
  height: 8px;
  border-radius: 5px;
  background: #444;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #27ae60;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #27ae60;
    cursor: pointer;
    border: none;
  }
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

const NumericInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
`;

const NumericInput = styled.input`
  // width: 100px;
  padding: 8px;
  // font-size: 1.2em;
  // font-weight: 600;
  text-align: center;
  border: 2px solid #444;
  border-radius: 5px;
  background-color: #222;
  color: white;

  &:focus {
    outline: none;
    border-color: var(--color--green);
  }
`;

const ActionButtonGroup = styled(ButtonGroup)`
  position: relative;
  bottom: auto;
  right: auto;
  justify-content: center;
`;

interface RotationAngleSelectorOverlayProps {
  initialRotation: number;
  rotation: number;
  onRotationChange: (angle: number) => void;
  onValidate: (angle: number) => void;
  onCancel: () => void;
  intl: IntlShape;
}

export const normalizeAngle = (angle: number): number => {
  const normalizationFactor = Math.sign(angle) * Math.floor(Math.abs(angle + 180) / 360);
  return angle - normalizationFactor * 360;
};

const RotationAngleSelectorOverlay: React.FC<RotationAngleSelectorOverlayProps> = ({
  initialRotation,
  rotation,
  onRotationChange,
  onValidate,
  onCancel,
  intl,
}) => {
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRotation = parseFloat(e.target.value);
    onRotationChange(newRotation);
  };

  const handleNumericInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string during typing
    if (value === '' || value === '-') return;

    const newRotation = parseFloat(value);
    if (!isNaN(newRotation)) {
      // Clamp to [-360, 360]
      const clampedRotation = Math.max(-180, Math.min(180, newRotation));
      onRotationChange(normalizeAngle(clampedRotation));
    }
  };

  const handleNumericInputBlur = () => {
    // Ensure valid value on blur
    if (isNaN(rotation)) {
      onRotationChange(normalizeAngle(initialRotation));
    }
  };

  const handleValidate = () => {
    onValidate(rotation);
  };

  const handleCancel = () => {
    onCancel();
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <div>
      <OverlayContainer
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseMove={stopPropagation}
        onPointerDown={stopPropagation}
        onPointerUp={stopPropagation}
        onPointerMove={stopPropagation}
        onDoubleClick={stopPropagation}
        onContextMenu={stopPropagation}
        onWheel={stopPropagation}
      >
        <div className="controls-section">
          <div className="angle-display">{rotation}°</div>

          <div className="slider-wrapper">
            {/* Les graduations en arrière-plan */}
            <div className="ticks-container">
              {[...Array(21)].map((_, i) => (
                <span key={i} className={i === 10 ? 'tick central' : 'tick'}></span>
              ))}
            </div>

            <input
              type="range"
              min="-180"
              max="180"
              step="1"
              value={rotation}
              onChange={handleSliderChange}
              className="slider"
            />
          </div>
        </div>
        {/* <SliderContainerWithDisplay>
          <RotationDisplay>{rotation.toFixed(0)}°</RotationDisplay>

          <SliderContainer>
            <Slider
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotation}
              onChange={handleSliderChange}
            />
          </SliderContainer>
        </SliderContainerWithDisplay> */}

        <BottomContainer>
          <NumericInputContainer>
            <NumericInput
              type="number"
              min="-180"
              max="180"
              step="1"
              value={rotation}
              onChange={handleNumericInputChange}
              onBlur={handleNumericInputBlur}
            />
          </NumericInputContainer>

          <ActionButtonGroup>
            <Button className="picoxia" onPointerDown={handleCancel}>
              <Icon name="cancel" />
              {intl.formatMessage(
                { id: 'tools.free_rotation.cancel' },
                { defaultMessage: 'Cancel' }
              )}
            </Button>
            <Button className="picoxia" positive onPointerDown={handleValidate}>
              <Icon name="check" />
              {intl.formatMessage(
                { id: 'tools.free_rotation.validate' },
                { defaultMessage: 'Validate' }
              )}
            </Button>
          </ActionButtonGroup>
        </BottomContainer>
      </OverlayContainer>
    </div>
  );
};

export default RotationAngleSelectorOverlay;
